import React, { useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useResource } from 'react-resource-router';

import { CommitSection } from 'src/components/pipelines/components/onboarding';
import {
  clearBuildConfiguration,
  requestCommitConfiguration,
  requestEnablePipelines,
  requestRepositoryBranchRestrictions,
} from 'src/components/pipelines/redux/actions/pipelines';
import {
  getAccount,
  getBuildConfiguration,
  getCapabilities,
  getRepository,
  getValidator,
} from 'src/components/pipelines/redux/selectors/pipelines';
import { getCurrentUserKey } from 'src/selectors/user-selectors';
import { publishTrackEvent } from 'src/utils/analytics/publish';

import { Allowance } from '../../models/Allowance';
import { loadAllowanceResource } from '../../resources/allowance';

type Props = {
  source: string;
};

const OnboardingCommit = ({ source }: Props) => {
  const dispatch = useDispatch();

  const account = useSelector(getAccount);
  const buildConfiguration = useSelector(getBuildConfiguration);
  const capabilities = useSelector(getCapabilities);
  const repository = useSelector(getRepository);
  const currentUserKey = useSelector(getCurrentUserKey);
  const validator = useSelector(getValidator);
  const { data: allowanceData } = useResource(loadAllowanceResource);

  const defaultAllowance = new Allowance();
  const allowance = allowanceData ?? defaultAllowance;

  useEffect(() => {
    dispatch(requestRepositoryBranchRestrictions(currentUserKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enablePipelines = useCallback(() => {
    dispatch(clearBuildConfiguration());
    dispatch(requestEnablePipelines());
    publishTrackEvent({
      actionSubjectId: '',
      actionSubject: 'builds',
      action: 'enabled',
      source,
      attributes: {
        repoUuid: repository.uuid,
        enforce2Fa: !allowance?.isTrusted,
      },
    });
  }, [allowance?.isTrusted, dispatch, repository.uuid, source]);

  const commitConfiguration = useCallback(
    (configuration: any, commitMessage: any) => {
      dispatch(requestCommitConfiguration(configuration, commitMessage));
      publishTrackEvent({
        actionSubjectId: '',
        actionSubject: 'configuration',
        action: 'added',
        source,
      });
    },
    [dispatch, source]
  );

  return (
    <CommitSection
      account={account}
      capabilities={capabilities}
      repository={repository}
      buildConfiguration={buildConfiguration}
      validator={validator}
      enablePipelines={enablePipelines}
      commitConfiguration={commitConfiguration}
    />
  );
};

export default React.memo(OnboardingCommit);
